/* Ezzyrol homepage v2: the shared runtime, window.EZH (foundation, 2026-10-07). No libraries.
   Documented in /tmp/lp2/build/FOUNDATION.md. Rendered (deferred) by snippets/ez-home-assets.liquid in every homepage
   section; the guard below makes every copy after the first a no-op, and because it is the first deferred script in each
   section it always runs before that section's own deferred ez-h-<name>.js.

   The rule for every moving part: a resting or hovering mouse NEVER stops it. It stops only for
     its own pause pill (or "pause all", localStorage ez-pause-all), an active drag (belts), keyboard focus inside it
     (:focus-visible only, never mouse focus), being off screen, a hidden tab, and reduced motion (per caller).

   API
     EZH.reduced                      live prefers-reduced-motion flag (getter)
     EZH.hidden                       live document.hidden (getter)
     EZH.onReduced(cb)                cb(isReduced) whenever the preference changes; returns an unsubscribe
     EZH.onVisibility(cb)             cb(isHidden) whenever the tab is hidden or shown; returns an unsubscribe
     EZH.onVisible(el, cb, {margin, threshold, once})   IntersectionObserver; cb(isVisible, entry); returns a stop
     EZH.warm(img)                    moves data-src / data-srcset (and <source data-srcset>) onto the real attributes
     EZH.afterHero(cb)                cb() once the hero's first photo (the LCP) has loaded, the page has loaded, or
                                      3 s have passed, whichever is first; at once on pages without the hero
     EZH.lazy(scope)                  watches img[data-ezh-lazy][data-src] inside scope and warms each one within about
                                      a screen of view, but only after EZH.afterHero (so below-the-fold cards never
                                      take bandwidth from the LCP photo on a slow phone, as loading=lazy does)
     EZH.mover(root, opts)            run/stop controller with holds and a pause pill (see below)
     EZH.stepper(root, opts)          a timed step cycler built on mover (colour cycle, set rows, announcements)
     EZH.belt(el, opts)               the requestAnimationFrame belt engine (el = .ezh-belt or its track)
     EZH.pauseAll(on)                 presses every pause control and remembers it on this device
     EZH.rail(el, {fade})             keeps a keyboard-focused item of a sideways scroll rail wholly in view
                                      (declarative: data-ezh-rail="<px under a right fade>")
     EZH.init(scope)                  auto-starts every [data-ezh-belt] and [data-ezh-rail] inside scope (runs at load
                                      and on shopify:section:load in the theme editor) */
(function () {
  'use strict';
  if (window.EZH) return;

  var doc = document, html = doc.documentElement;
  var me = doc.currentScript;           // this script tag: it carries the display face URLs (snippets/ez-home-assets)
  var mq = window.matchMedia ? matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };
  var PAUSE_KEY = 'ez-pause-all';
  var movers = [];
  var visHooks = [];
  var redHooks = [];
  var allPaused = false;
  try { allPaused = localStorage.getItem(PAUSE_KEY) === '1'; } catch (e) {}

  /* ---------------------------------------------------------------- small helpers */
  function $(s, r) { return (r || doc).querySelector(s); }
  function $$(s, r) { return Array.prototype.slice.call((r || doc).querySelectorAll(s)); }
  function num() { for (var i = 0; i < arguments.length; i++) { var v = parseFloat(arguments[i]); if (!isNaN(v)) return v; } return 0; }
  function now() { return performance.now(); }
  function isKbFocus(t) { try { return t.matches(':focus-visible'); } catch (e) { return false; } }
  function remover(list, f) { return function () { var k = list.indexOf(f); if (k > -1) list.splice(k, 1); }; }

  doc.addEventListener('visibilitychange', function () { visHooks.slice().forEach(function (f) { f(doc.hidden); }); });
  function onVisibility(cb) { visHooks.push(cb); return remover(visHooks, cb); }
  function redChange(e) { redHooks.slice().forEach(function (f) { f(e.matches); }); }
  if (mq.addEventListener) mq.addEventListener('change', redChange); else if (mq.addListener) mq.addListener(redChange);
  function onReduced(cb) { redHooks.push(cb); return remover(redHooks, cb); }

  /* IntersectionObserver wrapper. cb(isVisible, entry). Returns a function that stops observing. */
  function onVisible(el, cb, o) {
    o = o || {};
    if (!('IntersectionObserver' in window)) { cb(true); return function () {}; }
    var io = new IntersectionObserver(function (es) {
      es.forEach(function (en) {
        var v = en.isIntersecting && en.intersectionRatio >= (o.threshold || 0);
        cb(v, en);
        if (v && o.once) io.disconnect();
      });
    }, { rootMargin: o.margin || '0px', threshold: o.threshold || 0 });
    io.observe(el);
    return function () { io.disconnect(); };
  }

  function warm(img) {
    if (!img) return;
    var pic = img.parentElement;
    if (pic && pic.tagName === 'PICTURE') {
      $$('source[data-srcset]', pic).forEach(function (s) { s.srcset = s.dataset.srcset; s.removeAttribute('data-srcset'); });
    }
    if (img.dataset.srcset) { img.srcset = img.dataset.srcset; img.removeAttribute('data-srcset'); }
    if (img.dataset.src) { img.src = img.dataset.src; img.removeAttribute('data-src'); }
  }

  /* ---------------------------------------------------------------- after the hero photo (the LCP)
     Below-the-fold images wait for this, so on a slow phone they never share the bandwidth with the LCP photo. */
  var heroDone = false, heroCbs = [];
  function heroReady() {
    if (heroDone) return;
    heroDone = true;
    heroCbs.splice(0).forEach(function (f) { try { f(); } catch (e) {} });
  }
  function afterHero(cb) { if (heroDone) cb(); else heroCbs.push(cb); }
  // Waits for the hero photo AND the page's own load event (capped at 4 s). Since the phone photo is preloaded from
  // <head> (2026-10-07) it arrives early, and waiting on it alone started the display face, the nearby card images and
  // the belts' cold cards during the first paint, which a slow phone pays for in FCP and LCP.
  function watchHero() {
    var im = $('.ez-h-hero .ezhh-slide.is-on img, .ez-h-hero .ezhh-slide img');
    var imOk = !im || (im.complete && im.naturalWidth > 0), pageOk = doc.readyState === 'complete';
    function check() { if (imOk && pageOk) heroReady(); }
    if (!imOk) {
      var f = function () { imOk = true; check(); };
      im.addEventListener('load', f, { once: true });
      im.addEventListener('error', f, { once: true });
    }
    if (!pageOk) addEventListener('load', function () { pageOk = true; imOk = true; check(); }, { once: true });
    setTimeout(heroReady, 4000);
    check();
  }

  /* img[data-ezh-lazy][data-src]: warmed within about a screen of view, after the hero photo. Belts warm their own
     cards (they sit in overflow: hidden, which an observer's margin cannot see past), so this only takes the images
     an observer can see. */
  var lazyIO = null;
  function lazy(scope) {
    afterHero(function () {
      var ims = $$('img[data-ezh-lazy][data-src]', scope || doc);
      if (!ims.length) return;
      if (!('IntersectionObserver' in window)) { ims.forEach(warm); return; }
      if (!lazyIO) {
        lazyIO = new IntersectionObserver(function (es) {
          es.forEach(function (en) { if (en.isIntersecting) { lazyIO.unobserve(en.target); warm(en.target); } });
        }, { rootMargin: Math.round(innerHeight) + 'px 0px' });
      }
      ims.forEach(function (im) { lazyIO.observe(im); });
    });
  }

  /* ---------------------------------------------------------------- pause pill
     The pill swaps its word, its icon and its accessible name together ("Pause the slideshow" <-> "Play the
     slideshow"); it is not an aria-pressed toggle (a toggle's name must not change). data-paused drives the icon. */
  function syncPill(btn, paused) {
    if (!btn) return;
    btn.setAttribute('data-paused', paused ? 'true' : 'false');
    btn.removeAttribute('aria-pressed');
    var t = $('.ezh-pause__t', btn);
    if (t) t.textContent = paused ? 'Play' : 'Pause';
    var lab = btn.getAttribute('data-ezh-label');
    var cur = btn.getAttribute('aria-label') || '';
    btn.setAttribute('aria-label', lab ? (paused ? 'Play ' : 'Pause ') + lab : cur.replace(/^(Pause|Play) /, paused ? 'Play ' : 'Pause '));
  }

  /* ---------------------------------------------------------------- mover
     A controller for one moving part. It is RUNNING when no hold is set. Built-in holds:
       user     its pause pill (and EZH.pauseAll)
       off      the observed element is off screen (opts.observe, default root; opts.threshold, opts.margin;
                opts.visibility: false to skip)
       hidden   the tab is hidden
       kb       keyboard focus (:focus-visible) inside opts.focusRoot (default root), except on the pause pill itself;
                opts.focusHold: false to skip
       reduced  prefers-reduced-motion, unless opts.reduced === 'run' (then the caller calms itself down and reads
                EZH.reduced). With the default ('stop') the pause pill is hidden while reduced motion is on.
     Callers may add their own with m.hold('name', true/false) (for example 'loading').
     opts.start(m) / opts.stop(m)  called on every change between running and stopped
     opts.tick(dt, now, m)         optional: called every animation frame while running (dt in seconds, max .05)
     opts.onChange(running, m)     called after ANY hold changes (m.listen(fn) adds more; returns an unlisten)
     opts.pauseButton              element, selector inside root, or false; default root's first [data-ezh-pause]
     opts.pausedClass              class toggled on root while stopped (default 'is-paused'; false for none)
     Hover is deliberately not a hold. */
  function mover(root, o) {
    o = o || {};
    var holds = {}, running = false, raf = 0, last = 0, stops = [], listeners = [];
    var btn = o.pauseButton === false ? null
      : (o.pauseButton && o.pauseButton.nodeType === 1) ? o.pauseButton
      : $(typeof o.pauseButton === 'string' ? o.pauseButton : '[data-ezh-pause]', root);
    var reducedStops = o.reduced !== 'run';

    var m = {
      root: root,
      button: btn,
      get running() { return running; },
      get paused() { return !!holds.user; },
      is: function (n) { return !!holds[n]; },
      holds: function () { return Object.keys(holds); },
      hold: function (n, on) {
        if (!!holds[n] === !!on) return m;
        if (on) holds[n] = true; else delete holds[n];
        update();
        return m;
      },
      setPaused: function (p) { syncPill(btn, !!p); m.hold('user', p); return m; },
      listen: function (f) { listeners.push(f); return remover(listeners, f); },
      pause: function () { return m.setPaused(true); },
      play: function () { return m.setPaused(false); },
      toggle: function () { return m.setPaused(!holds.user); },
      destroy: function () {
        stops.forEach(function (f) { f(); });
        if (raf) cancelAnimationFrame(raf);
        raf = 0;
        var k = movers.indexOf(m); if (k > -1) movers.splice(k, 1);
      }
    };

    function frame(t) {
      var dt = Math.max(0, Math.min(0.05, (t - last) / 1000)); last = t;
      if (o.tick) o.tick(dt, t, m);
      raf = running ? requestAnimationFrame(frame) : 0;
    }
    function update() {
      var r = Object.keys(holds).length === 0;
      if (o.pausedClass !== false) root.classList.toggle(o.pausedClass || 'is-paused', !r);
      // .is-held: stopped on purpose (the pill, pause all, keyboard focus, the theme editor), not just off screen or
      // waiting for a photo. CSS uses it to mark the item on show with a static marker while nothing fills.
      root.classList.toggle('is-held', !!(holds.user || holds.kb || holds.editor));
      if (r !== running) {
        running = r;
        if (r) {
          if (o.start) o.start(m);
          if (o.tick && !raf) { last = now(); raf = requestAnimationFrame(frame); }
        } else {
          if (o.stop) o.stop(m);
          if (raf) { cancelAnimationFrame(raf); raf = 0; }
        }
      }
      if (o.onChange) o.onChange(running, m);
      listeners.slice().forEach(function (f) { f(running, m); });
    }

    // starting holds: nothing moves until the observer has said it is on screen
    if (allPaused) holds.user = true;
    if (doc.hidden) holds.hidden = true;
    if (reducedStops && mq.matches) holds.reduced = true;
    if (o.visibility !== false && 'IntersectionObserver' in window) holds.off = true;

    if (btn) {
      syncPill(btn, !!holds.user);
      var onClick = function (e) { e.preventDefault(); m.toggle(); };
      btn.addEventListener('click', onClick);
      stops.push(function () { btn.removeEventListener('click', onClick); });
      if (reducedStops) btn.hidden = mq.matches;     // nothing moves under reduced motion: no control to show
    }
    if (o.visibility !== false) {
      stops.push(onVisible(o.observe || root, function (v) { m.hold('off', !v); }, { threshold: o.threshold || 0, margin: o.margin || '0px' }));
    }
    stops.push(onVisibility(function (h) { m.hold('hidden', h); }));
    if (reducedStops) stops.push(onReduced(function (r) { if (btn) btn.hidden = r; m.hold('reduced', r); }));
    if (o.focusHold !== false) {
      var fr = o.focusRoot || root;
      var fin = function (e) { m.hold('kb', isKbFocus(e.target) && !(btn && btn.contains(e.target))); };
      var fout = function (e) { if (!fr.contains(e.relatedTarget)) m.hold('kb', false); };
      fr.addEventListener('focusin', fin); fr.addEventListener('focusout', fout);
      stops.push(function () { fr.removeEventListener('focusin', fin); fr.removeEventListener('focusout', fout); });
    }
    movers.push(m);
    update();
    return m;
  }

  /* ---------------------------------------------------------------- stepper
     EZH.stepper(root, opts): show item i, wait opts.interval ms, show i + 1, and so on, only while its mover runs.
       count, interval (ms, default 3000), index (first index, default 0)
       show(i, prev, how)    paint item i; how is 'auto' (timer) or 'user' (s.go called by a click)
       reduced: 'stop' (default: manual only under reduced motion) or 'run' with reducedInterval (ms)
       every mover option (pauseButton, observe, threshold, margin, focusHold, focusRoot, pausedClass, visibility),
       plus start(m) / stop(m) callbacks
     A pause keeps the time left in the current step, so a CSS progress bar paused with the same .is-paused class
     stays in step: the stepper writes --ezh-step (the step length) on root. s.go(k) jumps there and carries on with
     a full step. Returns { mover, go(k), next(), prev(), index, count, setCount(n) }. */
  function stepper(root, o) {
    o = o || {};
    var n = o.count || 0, i = o.index || 0, timer = 0, due = 0, left = 0;
    function ms() { return (o.reduced === 'run' && mq.matches && o.reducedInterval) ? o.reducedInterval : (o.interval || 3000); }
    function setVar() { root.style.setProperty('--ezh-step', ms() + 'ms'); }
    function clear() { if (timer) { clearTimeout(timer); timer = 0; } }
    function schedule() {
      clear();
      if (n < 2) return;
      due = now() + left;
      timer = setTimeout(function () { timer = 0; left = ms(); go(i + 1, 'auto'); }, left);
    }
    function go(k, how) {
      if (!n) return;
      var prev = i;
      i = ((k % n) + n) % n;
      if (how !== 'auto') left = ms();
      if (prev !== i && o.show) o.show(i, prev, how || 'user');
      if (s.mover && s.mover.running) schedule();
    }
    var s = {
      mover: null, go: go,
      next: function () { go(i + 1); }, prev: function () { go(i - 1); },
      get index() { return i; }, get count() { return n; },
      setCount: function (c) { n = c; if (i >= n) i = 0; }
    };
    left = ms(); setVar();
    s.mover = mover(root, {
      pauseButton: o.pauseButton, observe: o.observe, threshold: o.threshold, margin: o.margin, visibility: o.visibility,
      focusHold: o.focusHold, focusRoot: o.focusRoot, reduced: o.reduced, pausedClass: o.pausedClass,
      start: function (m) { schedule(); if (o.start) o.start(m); },
      stop: function (m) { if (timer) { clear(); left = Math.max(0, due - now()); } if (o.stop) o.stop(m); }
    });
    onReduced(function () { setVar(); });
    return s;
  }

  /* ---------------------------------------------------------------- rails: keep a keyboard-focused item in view
     A sideways scroll rail (price bands and the shelf on phones, the hero's category chips, a belt under reduced motion)
     clips its items; the browser only scrolls one into view when it is wholly out of view, so a Tab onto an item that
     sits half under the edge (or under a fade) would leave it mostly hidden. On keyboard focus (:focus-visible only),
     an item not wholly inside the rail's visible part is brought in: on a snap rail its start goes to the rail's
     scroll padding (the snap point, so the browser keeps it there); elsewhere the nearest edge moves by just enough.
     rail: the scroll box. opts.fade: px hidden under a right-edge fade (default: data-ezh-rail, else the scroll padding). */
  function bringIntoRail(rail, t, o) {
    o = o || {};
    if (!rail || !t || rail.scrollWidth <= rail.clientWidth + 1) return;
    var it = t;                                   // the rail's own item (its child, or a belt track's child)
    while (it.parentElement && it.parentElement !== rail && !(it.parentElement.parentElement === rail && it.parentElement.tagName === 'UL')) it = it.parentElement;
    if (!it.parentElement) return;
    var cs = getComputedStyle(rail);
    var pad = parseFloat(cs.scrollPaddingLeft) || parseFloat(cs.scrollPaddingInlineStart) || 0;
    var fade = o.fade != null ? o.fade : num(rail.getAttribute('data-ezh-rail'), pad);
    var r = it.getBoundingClientRect(), q = rail.getBoundingClientRect();
    var L = q.left + Math.min(pad, 2), R = q.right - fade;
    if (r.left >= L - 0.5 && r.right <= R + 0.5) return;
    var snap = cs.scrollSnapType && cs.scrollSnapType !== 'none';
    var dx;
    if (snap || r.left < L) dx = r.left - (q.left + pad);
    else dx = r.right - R;
    if (Math.abs(dx) < 1) return;
    try { rail.scrollBy({ left: dx, behavior: mq.matches ? 'auto' : 'smooth' }); } catch (e) { rail.scrollLeft += dx; }
  }
  function rail(el, o) {
    if (!el || el.__ezhRail) return;
    el.__ezhRail = true;
    el.addEventListener('focusin', function (e) {
      if (!isKbFocus(e.target)) return;
      var t = e.target;
      // after the browser's own focus scroll (it moves an item that was wholly out of view)
      requestAnimationFrame(function () { bringIntoRail(el, t, o); });
    });
  }

  /* ---------------------------------------------------------------- pause all (the footer switch) */
  function pauseAll(on, save) {
    allPaused = !!on;
    html.classList.toggle('is-ezh-allpaused', allPaused);
    $$('[data-ezh-pause-all]').forEach(function (b) { b.setAttribute('aria-pressed', allPaused ? 'true' : 'false'); });
    movers.forEach(function (m) { m.setPaused(allPaused); });
    if (save !== false) { try { localStorage.setItem(PAUSE_KEY, allPaused ? '1' : '0'); } catch (e) {} }
  }
  doc.addEventListener('click', function (e) {
    var b = e.target.closest && e.target.closest('[data-ezh-pause-all]');
    if (b) pauseAll(b.getAttribute('aria-pressed') !== 'true');
  });

  /* ---------------------------------------------------------------- belt
     EZH.belt(el, opts). el: the .ezh-belt viewport (or its .ezh-belt__track).
     opts (data attributes on el in brackets):
       speed      px/s cruise, default 36 [data-speed]. Phones (< 750 px) run at phoneFactor (default .85) of it.
       direction  1 or 'left' (content travels left, default), -1 or 'right' [data-dir]
       hover      percent of cruise under a hovering mouse, default 33 [data-hover]. Never 0, never a stop.
       mover      share one EZH.mover between belts (one pause pill for two rows); default: its own mover on
                  root (opts.root, else closest [data-ezh-section], else .shopify-section) with that root's pill
       pauseButton  passed to the mover it creates
     The cruise is a compositor animation (Web Animations, a linear translate loop over one set of cards), so a moving
     belt costs the main thread nothing per frame. Speed changes (hover, pause, play, back on screen) ease the
     animation's playbackRate for a second or two; only then, and while a pointer drags or a release flings, does a
     requestAnimationFrame loop run (the drag writes the transform itself). Off screen or in a hidden tab the
     animation is paused at once. A drag never fires a link. Keyboard focus holds it and brings the focused card into
     view. A sideways trackpad swipe or shift + wheel scrubs it. A release flings only with the speed of the last
     100 ms of the drag, and not at all after the pointer has rested. Reduced motion: no clones, a native scroll-snap
     rail that touch swipes, a mouse drags and a wheel scrolls; keyboard focus scrolls the focused card into view.
     Card images with data-src load when within about half a screen, after the hero photo (EZH.afterHero). */
  var belts = [];
  var beltId = 0;
  var hasWAAPI = typeof Element !== 'undefined' && typeof Element.prototype.animate === 'function';
  function belt(el, o) {
    o = o || {};
    if (el.classList.contains('ezh-belt__track')) el = el.parentElement;
    if (el.__ezhBelt) return el.__ezhBelt;
    var track = $('.ezh-belt__track', el) || el.firstElementChild;
    if (!track) return null;
    var ds = el.dataset;
    var speed = num(o.speed, ds.speed, 36);
    var d = o.direction != null ? o.direction : ds.dir;
    var dir = (d === 'right' || d === -1 || d === '-1') ? -1 : 1;
    var hv = num(o.hover, ds.hover, 33); if (hv > 1) hv /= 100; hv = Math.max(0.1, hv);
    var phone = o.phoneFactor != null ? o.phoneFactor : 0.85;
    var root = o.root || el.closest('[data-ezh-section]') || el.closest('.shopify-section') || el.parentElement;
    var id = 'kb' + (++beltId);
    var m = o.mover || mover(root, { pauseButton: o.pauseButton, focusHold: false, observe: el, margin: '0px' });

    var b = {
      el: el, track: track, mover: m,
      x: 0, v: 0, target: 0, fling: 0, drag: null, hover: false, raf: 0, t: 0, lw: 0,
      setW: 0, isStatic: false, near: false, cold: [],
      anim: null, rate: 0, rateRaf: 0, rateT: 0, warmTimer: 0, sdrag: null
    };
    function base() { return speed * (innerWidth < 750 ? phone : 1); }
    function paint() { track.style.transform = 'translate3d(' + b.x.toFixed(2) + 'px,0,0)'; }
    function originals() { return Array.prototype.filter.call(track.children, function (c) { return !c.hasAttribute('data-ezh-clone'); }); }
    function clones() { return Array.prototype.filter.call(track.children, function (c) { return c.hasAttribute('data-ezh-clone'); }); }
    function addSet(items) {
      items.forEach(function (li) {
        var c = li.cloneNode(true);
        c.setAttribute('aria-hidden', 'true'); c.setAttribute('data-ezh-clone', ''); c.inert = true;
        $$('a, button, input, select, textarea, [tabindex]', c).forEach(function (a) { a.tabIndex = -1; });
        $$('[id]', c).forEach(function (x) { x.removeAttribute('id'); });
        track.appendChild(c);
      });
    }
    function measure() {
      var items = originals();
      if (!items.length) { b.setW = 0; return; }
      var first = clones()[0];
      if (first) b.setW = first.offsetLeft - items[0].offsetLeft;
      // one copy is enough while a set is wider than the viewport; short sets get more copies
      var need = Math.max(1, Math.ceil((el.clientWidth + 1) / Math.max(1, b.setW)));
      var have = clones().length / items.length;
      while (have < need) { addSet(items); have++; }
      rebuildCold();
    }
    function rebuildCold() {
      b.cold = $$('img[data-src]', track).map(function (im) { return { im: im, li: im.closest('.ezh-belt__track > *') || im }; });
    }
    function wrap() {
      if (b.setW <= 0) return;
      while (b.x <= -b.setW) b.x += b.setW;
      while (b.x > 0) b.x -= b.setW;
    }

    /* ---- the compositor cruise */
    function animX() {
      var p = b.anim.effect.getComputedTiming().progress;
      if (p == null || isNaN(p)) p = 0;
      return dir > 0 ? -b.setW * p : -b.setW * (1 - p);
    }
    function curX() { return b.anim ? animX() : b.x; }
    function stopRate() { if (b.rateRaf) { cancelAnimationFrame(b.rateRaf); b.rateRaf = 0; } }
    function killAnim() {                  // hand the position back to the inline transform (drag, fling, focus, resize)
      stopRate();
      if (!b.anim) return;
      b.x = animX(); b.v = b.rate * base();
      b.anim.cancel(); b.anim = null;
      wrap(); paint();
    }
    function makeAnim() {
      if (!hasWAAPI || b.setW <= 0 || b.isStatic) return false;
      wrap();
      var dur = b.setW / base() * 1000;
      var a = 'translate3d(0px,0,0)', z = 'translate3d(' + (-b.setW) + 'px,0,0)';
      b.anim = track.animate(dir > 0 ? [{ transform: a }, { transform: z }] : [{ transform: z }, { transform: a }],
        { duration: dur, iterations: Infinity, easing: 'linear' });
      var p = dir > 0 ? -b.x / b.setW : (b.x + b.setW) / b.setW;
      b.anim.currentTime = Math.max(0, Math.min(0.99999, p)) * dur;
      b.rate = Math.max(0, b.v / base());
      b.anim.playbackRate = b.rate;
      if (b.rate === 0) b.anim.pause();
      return true;
    }
    function rateStep(t) {
      var dt = Math.max(0, Math.min(0.05, (t - b.rateT) / 1000)); b.rateT = t;   // a frame time can precede now()
      b.rateRaf = 0;
      if (!b.anim) return;
      var goal = b.target / base();
      b.rate = Math.max(0, b.rate + (goal - b.rate) * Math.min(1, dt * 2.6));
      var done = Math.abs(goal - b.rate) < 0.004;
      if (done) b.rate = goal;
      if (b.rate > 0 && b.anim.playState !== 'running') b.anim.play();
      b.anim.playbackRate = b.rate;
      if (done) { if (b.rate === 0) b.anim.pause(); return; }
      b.rateRaf = requestAnimationFrame(rateStep);
    }
    function startRate() { if (!b.rateRaf && b.anim) { b.rateT = now(); b.rateRaf = requestAnimationFrame(rateStep); } }
    function warmTick() {
      warmRange();
      if (!b.cold.length || !b.anim || b.anim.playState !== 'running') { clearInterval(b.warmTimer); b.warmTimer = 0; }
    }
    function startWarm() { if (!b.warmTimer && b.cold.length && b.near) b.warmTimer = setInterval(warmTick, 300); }

    // decide what drives the belt now: the compositor animation, the drag and fling loop, or nothing
    function drive() {
      if (b.isStatic) return;
      if (b.raf) return;                                         // a drag or a fling owns it until it settles
      if (m.is('off') || m.is('hidden')) {
        stopRate();
        if (b.anim && b.anim.playState === 'running') b.anim.pause();
        return;
      }
      if (!b.anim) {
        if (b.target === 0 && b.v < 0.05) return;
        if (!makeAnim()) { startRaf(); return; }                 // no Web Animations: the old frame loop
      }
      if (b.target > 0 && b.anim.playState !== 'running') { if (b.rate === 0) b.rate = 0.0001; b.anim.play(); }
      if (Math.abs(b.target / base() - b.rate) > 0.001) startRate();
      startWarm();
    }
    function retarget() { b.target = m.running ? (b.hover ? base() * hv : base()) : 0; drive(); }

    /* ---- the frame loop: drag, fling (and the fallback cruise without Web Animations) */
    function warmRange() {
      if (!b.near || !b.cold.length || !heroDone) return;
      var W = innerWidth, x = curX();
      b.cold = b.cold.filter(function (c) {
        if (!c.im.dataset.src) return false;
        var cx = c.li.offsetLeft + x;
        if (cx < W * 1.5 && cx + c.li.offsetWidth > -W * 0.5) { warm(c.im); return false; }
        return true;
      });
    }
    function tick(t) {
      var dt = Math.max(0, Math.min(0.05, (t - b.t) / 1000)); b.t = t;
      if (!b.drag) {
        b.v += (b.target - b.v) * Math.min(1, dt * 2.6);
        b.x -= dir * b.v * dt + b.fling * dt;
        b.fling *= Math.pow(0.04, dt);
        if (Math.abs(b.fling) < 2) b.fling = 0;
      }
      wrap(); paint();
      if (t - b.lw > 300) { b.lw = t; warmRange(); }
      var resting = !b.drag && b.target === 0 && b.v < 0.05 && !b.fling;
      if (resting) b.v = 0;
      b.raf = 0;
      if (b.isStatic || m.is('off') || m.is('hidden') || resting) return;
      if (!b.drag && !b.fling && hasWAAPI) { drive(); return; }   // settled: back to the compositor
      b.raf = requestAnimationFrame(tick);
    }
    function startRaf() {
      if (b.raf || b.isStatic || m.is('off') || m.is('hidden')) return;
      b.t = now();
      b.raf = requestAnimationFrame(tick);
    }
    function stopAll() {
      stopRate();
      if (b.raf) { cancelAnimationFrame(b.raf); b.raf = 0; }
      if (b.warmTimer) { clearInterval(b.warmTimer); b.warmTimer = 0; }
      if (b.anim) { b.anim.cancel(); b.anim = null; }
    }

    function toStatic() {
      stopAll();
      b.isStatic = true; b.drag = null; b.v = 0; b.fling = 0; b.x = 0; b.rate = 0;
      clones().forEach(function (c) { c.remove(); });
      track.style.transform = '';
      el.classList.add('is-static');
      el.classList.remove('is-dragging');
      rebuildCold();
      if (b.near) afterHero(function () { b.cold.forEach(function (c) { warm(c.im); }); });
    }
    function toMoving() {
      b.isStatic = false;
      el.classList.remove('is-static');
      el.scrollLeft = 0;
      addSet(originals());
      measure();
      b.x = dir > 0 ? 0 : -b.setW;
      paint(); warmRange(); retarget();
    }

    // mover changes: ease to the new target; off screen or in a hidden tab the animation pauses at once
    var unlisten = m.listen(function () {
      retarget();
      if ((m.is('off') || m.is('hidden')) && b.raf) { cancelAnimationFrame(b.raf); b.raf = 0; }
      else if (!b.anim && (b.drag || b.fling)) startRaf();
    });

    // vertical warm-up: within a screen height of the viewport, start loading the cards in reach (after the hero photo)
    var stopNear = onVisible(el, function (v) {
      if (!v) return;
      afterHero(function () {
        b.near = true;
        if (b.isStatic) b.cold.forEach(function (c) { warm(c.im); }); else { warmRange(); startWarm(); }
      });
    }, { margin: Math.round(innerHeight) + 'px 0px', once: true });

    // pointer: hover slows (never stops), drag scrubs, release flings, a drag never opens a card
    function blockClick() {
      var block = function (ev) { ev.preventDefault(); ev.stopPropagation(); };
      el.addEventListener('click', block, { capture: true, once: true });
      setTimeout(function () { el.removeEventListener('click', block, { capture: true }); }, 60);
    }
    el.addEventListener('pointerenter', function (e) { if (e.pointerType === 'mouse') { b.hover = true; retarget(); } });
    el.addEventListener('pointerleave', function (e) { if (e.pointerType === 'mouse') { b.hover = false; retarget(); } });
    el.addEventListener('pointerdown', function (e) {
      if (e.button !== 0) return;
      if (b.isStatic) {
        // the still rail: a mouse drags it sideways (touch scrolls it natively)
        if (e.pointerType === 'mouse') b.sdrag = { x0: e.clientX, sl: el.scrollLeft, moved: false, id: e.pointerId };
        return;
      }
      killAnim();
      b.drag = { x0: e.clientX, y0: e.clientY, start: b.x, lt: now(), hist: [[now(), e.clientX]], moved: false, id: e.pointerId };
      b.fling = 0; startRaf();
    });
    el.addEventListener('pointermove', function (e) {
      var s = b.sdrag;
      if (s && b.isStatic) {
        if (e.pointerId !== s.id) return;
        var sx = e.clientX - s.x0;
        if (!s.moved) {
          if (Math.abs(sx) < 6) return;
          s.moved = true; el.classList.add('is-dragging');
          try { el.setPointerCapture(e.pointerId); } catch (err) {}
        }
        el.scrollLeft = s.sl - sx;
        return;
      }
      var g = b.drag;
      if (!g || e.pointerId !== g.id) return;
      var dx = e.clientX - g.x0;
      if (!g.moved) {
        if (Math.abs(e.clientY - g.y0) > Math.abs(dx) && Math.abs(e.clientY - g.y0) > 8) { b.drag = null; drive(); return; }
        if (Math.abs(dx) < 6) return;
        g.moved = true; el.classList.add('is-dragging');
        try { el.setPointerCapture(e.pointerId); } catch (err) {}
      }
      // the release speed comes from the moves of the last 100 ms (see end()), never from one stale pair
      var t = now();
      g.lt = t;
      g.hist.push([t, e.clientX]);
      while (g.hist.length > 2 && t - g.hist[0][0] > 100) g.hist.shift();
      b.x = g.start + dx;
      startRaf();
    });
    // release speed in px/s: the distance over the moves of the last 100 ms, faded to nothing when the pointer has
    // rested (no move for 80 ms or more), so a drag that stops on a card leaves that card where it is
    function releaseSpeed(g) {
      var t = now(), idle = t - g.lt;
      if (idle >= 80 || g.hist.length < 2) return 0;
      var h = g.hist.filter(function (p) { return t - p[0] <= 100 + idle; });
      if (h.length < 2) return 0;
      var a = h[0], z = h[h.length - 1], span = z[0] - a[0];
      if (span < 8) return 0;
      return (z[1] - a[1]) / span * 1000 * (1 - idle / 80);
    }
    function end() {
      var s = b.sdrag;
      if (s) {
        b.sdrag = null;
        if (s.moved) { el.classList.remove('is-dragging'); blockClick(); }
        return;
      }
      var g = b.drag;
      if (!g) return;
      b.drag = null;
      if (g.moved) {
        el.classList.remove('is-dragging');
        b.fling = -releaseSpeed(g) * 0.9;
        if (Math.abs(b.fling) < 2) b.fling = 0;
        blockClick();
      }
      startRaf();
    }
    el.addEventListener('pointerup', end);
    el.addEventListener('pointercancel', end);
    el.addEventListener('lostpointercapture', function () { if (b.sdrag) end(); });
    el.addEventListener('dragstart', function (e) { e.preventDefault(); });

    // a sideways trackpad swipe (or shift + mouse wheel) scrubs the belt like a drag, and is taken from the browser
    // so it never becomes a history swipe; a vertical wheel is left alone, so the page scrolls as usual. The cruise
    // eases back in once the wheel has been still for 160 ms (a trackpad sends its own momentum before that).
    el.addEventListener('wheel', function (e) {
      if (b.isStatic || b.drag) return;               // the still rail scrolls natively
      var dx = e.deltaX, dy = e.deltaY;
      if (e.shiftKey && !dx) { dx = dy; dy = 0; }
      if (!dx || Math.abs(dx) <= Math.abs(dy)) return;
      e.preventDefault();
      if (e.deltaMode === 1) dx *= 16; else if (e.deltaMode === 2) dx *= el.clientWidth;
      killAnim();
      if (b.raf) { cancelAnimationFrame(b.raf); b.raf = 0; }
      b.fling = 0; b.v = 0;
      b.x -= dx; wrap(); paint();
      var t = now(); if (t - b.lw > 300) { b.lw = t; warmRange(); }
      clearTimeout(b.wheelT);
      b.wheelT = setTimeout(function () { b.wheelT = 0; drive(); }, 160);
    }, { passive: false });

    // keyboard focus: hold this belt and bring the focused card fully into view (on the still rail: scroll it there)
    el.addEventListener('focusin', function (e) {
      if (!isKbFocus(e.target)) { m.hold(id, false); return; }
      if (!b.isStatic) { killAnim(); if (b.raf) { cancelAnimationFrame(b.raf); b.raf = 0; } b.v = 0; b.rate = 0; b.fling = 0; }
      m.hold(id, true);
      if (b.isStatic) { var ft = e.target; requestAnimationFrame(function () { bringIntoRail(el, ft); }); return; }
      showCard(e.target.closest('.ezh-belt__track > *'));
    });
    function showCard(li) {
      if (!li || b.isStatic) return;
      var r = li.getBoundingClientRect(), box = el.getBoundingClientRect();
      // never past x = 0: the first card is fully in view at its own padding (under 750 px that is 20 px, less than 24)
      if (r.left < box.left + 24) b.x = Math.min(b.x + box.left + 24 - r.left, 0);
      else if (r.right > box.right - 24) b.x -= r.right - box.right + 24;
      wrap(); paint();
    }
    // theme editor: selecting a card's block (the reviews band) holds the row with that card in view until deselected
    el.addEventListener('shopify:block:select', function (e) {
      var li = e.target.closest && e.target.closest('.ezh-belt__track > *');
      if (!li || li.hasAttribute('data-ezh-clone')) return;
      if (!b.isStatic) { killAnim(); if (b.raf) { cancelAnimationFrame(b.raf); b.raf = 0; } b.v = 0; b.rate = 0; b.fling = 0; }
      m.hold('editor', true);
      if (b.isStatic) bringIntoRail(el, li, { fade: 0 }); else showCard(li);
    });
    el.addEventListener('shopify:block:deselect', function () { m.hold('editor', false); });
    el.addEventListener('focusout', function (e) { if (!el.contains(e.relatedTarget)) m.hold(id, false); });
    // the browser may scroll the overflow box itself to show a focused card: fold that back into the transform
    el.addEventListener('scroll', function () {
      if (b.isStatic || !el.scrollLeft) return;
      killAnim();
      b.x -= el.scrollLeft; el.scrollLeft = 0; wrap(); paint(); drive();
    });

    var stopRed = onReduced(function (r) { if (r && !b.isStatic) toStatic(); else if (!r && b.isStatic) toMoving(); });
    b.refresh = function () {
      if (!b.isStatic) { killAnim(); if (b.raf) { cancelAnimationFrame(b.raf); b.raf = 0; } measure(); wrap(); paint(); warmRange(); }
      retarget();
    };
    b.setSpeed = function (px) { killAnim(); speed = px; retarget(); };
    b.setDirection = function (v) { killAnim(); dir = (v === 'right' || v < 0) ? -1 : 1; retarget(); };
    b.position = curX;
    b.destroy = function () {
      stopAll(); stopNear(); stopRed(); unlisten();
      clones().forEach(function (c) { c.remove(); });
      track.style.transform = '';
      if (!o.mover) m.destroy();
      var k = belts.indexOf(b); if (k > -1) belts.splice(k, 1);
      delete el.__ezhBelt;
    };

    el.__ezhBelt = b;
    belts.push(b);
    if (mq.matches) toStatic(); else toMoving();
    return b;
  }

  var rz = 0;
  addEventListener('resize', function () {
    clearTimeout(rz);
    rz = setTimeout(function () { belts.forEach(function (b) { b.refresh(); }); }, 150);
  });
  addEventListener('load', function () { belts.forEach(function (b) { b.refresh(); }); });

  /* ---------------------------------------------------------------- declarative start
     Every [data-ezh-belt] without data-ezh-manual starts itself. Belts in one section share one mover, so the
     section's first [data-ezh-pause] pill stops all its rows (the reviews band's two rows). */
  function init(scope) {
    $$('[data-ezh-belt]', scope || doc).forEach(function (el) {
      if (el.__ezhBelt || el.hasAttribute('data-ezh-manual')) return;
      var root = el.closest('[data-ezh-section]') || el.closest('.shopify-section') || el.parentElement;
      var m = root.__ezhMover;
      if (!m) m = root.__ezhMover = mover(root, { focusHold: false, observe: root, margin: '0px' });
      belt(el, { mover: m, root: root });
    });
    $$('[data-ezh-rail]', scope || doc).forEach(function (el) { rail(el); });
  }
  doc.addEventListener('shopify:section:load', function (e) { init(e.target); lazy(e.target); });
  doc.addEventListener('shopify:section:unload', function (e) {
    belts.slice().forEach(function (b) { if (e.target.contains(b.el)) b.destroy(); });
    movers.slice().forEach(function (m) { if (e.target.contains(m.root)) m.destroy(); });
  });

  /* ---------------------------------------------------------------- display faces (snippets/ez-home-assets)
     The upright face is added after the hero photo (EZH.afterHero), or at once when this device has loaded it before
     (assumed cached, so a returning visitor never sees the swap); the hero's inline loader may have done it already.
     The italic is added when a part that draws it (the colour cycle, the trolley review) comes within a screen. */
  function face(url, style, ok) {
    if (!url || !window.FontFace || !doc.fonts) return;
    var f = new FontFace('EZH Newsreader', 'url(' + url + ') format("woff2")', { style: style, weight: '300 400', display: 'swap' });
    doc.fonts.add(f);
    f.load().then(ok || function () {}).catch(function () {});
  }
  (function () {
    var up = me && me.getAttribute('data-ezh-face'), it = me && me.getAttribute('data-ezh-italic');
    if (up && !window.__ezhFace) {
      window.__ezhFace = 1;
      var seen = false; try { seen = localStorage.getItem('ezh-face') === up; } catch (e) {}
      var addUp = function () { face(up, 'normal', function () { try { localStorage.setItem('ezh-face', up); } catch (e) {} }); };
      if (seen) addUp(); else afterHero(addUp);
    }
    if (it && !window.__ezhItalic) {
      // only when a part that draws italic (the trolley review, the colour names) comes within about a screen, so a
      // visitor who never scrolls that far never fetches the 93 KB file
      window.__ezhItalic = 1;
      var stops = [];
      var addIt = function () {
        if (addIt.done) return; addIt.done = 1;
        stops.forEach(function (f) { f(); });
        face(it, 'italic');
      };
      var users = $$('.ez-h-colour, .ezt-f__q, [data-ezh-italic-user]');
      users.forEach(function (u) { stops.push(onVisible(u, function (v) { if (v) addIt(); }, { margin: Math.round(innerHeight) + 'px 0px' })); });
    }
  })();

  window.EZH = {
    version: '2.0.0',
    get reduced() { return !!mq.matches; },
    get hidden() { return doc.hidden; },
    get allPaused() { return allPaused; },
    onReduced: onReduced,
    onVisibility: onVisibility,
    onVisible: onVisible,
    warm: warm,
    afterHero: afterHero,
    lazy: lazy,
    mover: mover,
    stepper: stepper,
    belt: belt,
    pauseAll: pauseAll,
    rail: rail,
    bringIntoRail: bringIntoRail,
    init: init,
    movers: movers,
    belts: function () { return belts.slice(); }
  };
  if (allPaused) html.classList.add('is-ezh-allpaused');
  function boot() { watchHero(); init(); lazy(); }
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', boot);
  else boot();
})();

/* ---------------------------------------------------------------- legacy: the measured size hero
   sections/ez-measured-hero.liquid (collection.sets-explorer) still loads this file for its range rotation
   ([data-ez-hero]). Kept unchanged in behaviour so that page does not regress; it is not a homepage section and no
   homepage v2 section may use data-ez-hero. Guarded so a second copy of this file never binds it twice. */
(function () {
  'use strict';
  var hero = document.querySelector('[data-ez-hero]');
  if (!hero || hero.__ezLegacy) return;
  hero.__ezLegacy = true;
  var slides = Array.prototype.slice.call(hero.querySelectorAll('[data-ez-slide]'));
  var tabs = Array.prototype.slice.call(hero.querySelectorAll('[data-ez-tab]'));
  if (slides.length < 2) return;
  var lede = hero.querySelector('[data-ez-lede]'), caption = hero.querySelector('[data-ez-caption]');
  var pause = hero.querySelector('[data-ez-pause]');
  var ms = (parseInt(hero.getAttribute('data-interval'), 10) || 6) * 1000;
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  var i = 0, timer = null, paused = false, stopped = false, hover = false, focused = false, inView = true;
  function show(n, focusTab) {
    i = (n + slides.length) % slides.length;
    slides.forEach(function (s, j) {
      var on = j === i;
      if (on) s.setAttribute('data-active', ''); else s.removeAttribute('data-active');
      s.inert = !on;
      if (on) { var img = s.querySelector('img[loading="lazy"]'); if (img) img.loading = 'eager'; }
    });
    tabs.forEach(function (t, j) { t.setAttribute('aria-selected', j === i ? 'true' : 'false'); t.tabIndex = j === i ? 0 : -1; });
    if (focusTab && tabs[i]) tabs[i].focus();
    var t = tabs[i], rail = t && t.parentElement;
    if (rail && rail.scrollWidth > rail.clientWidth) rail.scrollTo({ left: t.offsetLeft - rail.offsetLeft - (rail.clientWidth - t.offsetWidth) / 2, behavior: reduced.matches ? 'auto' : 'smooth' });
    if (lede) lede.textContent = slides[i].getAttribute('data-lede');
    if (caption) caption.textContent = slides[i].getAttribute('data-caption');
    var nxt = slides[(i + 1) % slides.length].querySelector('img[loading="lazy"]'); if (nxt) nxt.loading = 'eager';
  }
  function running() { return !paused && !stopped && !hover && !focused && inView && !reduced.matches && !document.hidden; }
  function tick() { clearTimeout(timer); if (running()) timer = setTimeout(function () { show(i + 1); tick(); }, ms); }
  function setPaused(p) { paused = p; if (pause) pause.setAttribute('aria-pressed', p ? 'true' : 'false'); tick(); }
  tabs.forEach(function (t, j) {
    t.addEventListener('click', function () { stopped = true; setPaused(true); show(j); });
    t.addEventListener('keydown', function (e) {
      var d = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
      if (e.key === 'Home') { e.preventDefault(); stopped = true; setPaused(true); show(0, true); return; }
      if (e.key === 'End') { e.preventDefault(); stopped = true; setPaused(true); show(slides.length - 1, true); return; }
      if (!d) return;
      e.preventDefault(); stopped = true; setPaused(true); show(i + d, true);
    });
  });
  if (pause) pause.addEventListener('click', function () { stopped = false; setPaused(!paused); });
  hero.addEventListener('pointerenter', function () { hover = true; tick(); });
  hero.addEventListener('pointerleave', function () { hover = false; tick(); });
  hero.addEventListener('focusin', function () { focused = true; tick(); });
  hero.addEventListener('focusout', function (e) { if (!hero.contains(e.relatedTarget)) { focused = false; tick(); } });
  if ('IntersectionObserver' in window) new IntersectionObserver(function (es) { inView = es[0].isIntersecting; tick(); }, { threshold: 0.15 }).observe(hero);
  document.addEventListener('visibilitychange', tick);
  if (reduced.addEventListener) reduced.addEventListener('change', tick);
  if (reduced.matches && pause) { setPaused(true); pause.hidden = true; }
  show(0);
  tick();
})();
